import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { NatsProvider } from "contexts/NatsProvider";
import { useOverlays } from "contexts/OverlayContext";
import {
    useKeyboardCommand,
    HOTKEYS_SCOPES,
} from "@hooks/keyboard/useKeyboardCommand";
import { KeyboardCommandsModal } from "@hooks/keyboard/KeyboardCommandsModal";
import { useMobileBlockScreen } from "@hooks/useMobileView";
import { useApplicationView, ApplicationView } from "@hooks/useApplicationView";
import { usePageTracking } from "@hooks/usePageTracking";
import { resolveLoginRedirectTarget } from "@lib/redirectAfterLogin";
import { StateAwareNavigate } from "@components/routes/StateAwareNavigate";
import { MobileBlock } from "@components/mobileBlock/MobileBlock";
import { LaptopIcon } from "@icons";
import { TransactionsPrefetcher } from "./transactions/usePrefetchTransactions";
import { usePrefetchTransactionViews } from "./transactions/usePrefetchTransactionViews";
import { NewConnectionHandler } from "./plaid/NewConnection/NewConnectionHandler";
import { OnboardingRoutes } from "./routes/OnboardingRoutes";
import { MainRoutes } from "./routes/MainRoutes";
import { PersonalViewRoutes } from "./routes/PersonalViewRoutes";
import { OrganizationRoutes } from "./routes/OrganizationRoutes";
import { PlanManagementProvider } from "./billing/PlanManagement/PlanManagementProvider";
import { ChatProvider } from "./mainInterface/chat/ChatProvider";
import { SupportChatProvider } from "./supportChat/SupportChatProvider";
import { ClientActionsProvider } from "./organizationView/clients/ClientActionsProvider";
import { useIdentification } from "./analytics/useIdentification";
import { FeatureFlagsIdentityContext } from "./analytics/featureFlagsIdentity";
import { DocumentTitleProvider } from "./DocumentTitle";
import { SettingsProvider } from "./settings/SettingsProvider.provider";
import { DemoFeatureFlagsPanel } from "./featureFlags/DemoFeatureFlagsPanel";
import { DemoDataSeedingGate } from "./demoData/DemoDataSeedingGate";

const ALLOWED_ON_TABLET = ["/onboarding", "/invoicing/preview"];

export const PrivateInterface: React.FC = () => {
    const showMobileBlockScreen = useMobileBlockScreen();
    const routeGroup = useApplicationView();
    // The useAppConfig call is to ensure that the app is observing the query, and the data does not get cleared after the default gc time elapses 5min

    const location = useLocation();
    const [redirectTarget, setRedirectTarget] = useState(() =>
        resolveLoginRedirectTarget(window.location.hash),
    );
    const currentPath = `${location.pathname}${location.search}`;
    usePageTracking();
    usePrefetchTransactionViews();
    const featureFlagsIdentity = useIdentification();
    const { openOverlay } = useOverlays();

    const showMobileBlock = useMemo(
        () =>
            showMobileBlockScreen &&
            !ALLOWED_ON_TABLET.some((p) => location.pathname.startsWith(p)),
        [showMobileBlockScreen, location.pathname],
    );

    const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

    useKeyboardCommand({
        keys: "alt+h",
        callback: () => {
            setShowKeyboardShortcuts(true);
        },
        options: {
            description: "Show keyboard shortcuts",
            enableOnFormTags: true,
            scopes: [HOTKEYS_SCOPES.GLOBAL],
        },
    });

    if (redirectTarget && redirectTarget !== currentPath) {
        return <StateAwareNavigate to={redirectTarget} replace />;
    }

    if (redirectTarget) {
        setRedirectTarget(null);
    }

    const renderRoutes = () => {
        switch (routeGroup) {
            case ApplicationView.ONBOARDING:
                return <OnboardingRoutes />;
            case ApplicationView.MAIN:
                return (
                    <DemoDataSeedingGate>
                        <MainRoutes />
                    </DemoDataSeedingGate>
                );
            case ApplicationView.PERSONAL_VIEW:
                return <PersonalViewRoutes />;
            case ApplicationView.ORGANIZATION_VIEW:
                return (
                    <ClientActionsProvider>
                        <OrganizationRoutes />
                    </ClientActionsProvider>
                );
            default:
                return null;
        }
    };

    return (
        <FeatureFlagsIdentityContext.Provider value={featureFlagsIdentity}>
            <NatsProvider>
                <DocumentTitleProvider>
                    <ChatProvider>
                        <SupportChatProvider>
                            <PlanManagementProvider>
                                <SettingsProvider
                                    openGlobalOverlay={openOverlay}
                                >
                                    {renderRoutes()}
                                    <DemoFeatureFlagsPanel />

                                    {showMobileBlock && (
                                        <MobileBlock
                                            fullscreen
                                            icon={<LaptopIcon />}
                                            heading="Kick isn't available on mobile just yet"
                                            showLogoutButton
                                        >
                                            <p>
                                                Please log in on a desktop
                                                browser.
                                            </p>
                                        </MobileBlock>
                                    )}
                                </SettingsProvider>

                                {showKeyboardShortcuts && (
                                    <KeyboardCommandsModal
                                        onClose={() =>
                                            setShowKeyboardShortcuts(false)
                                        }
                                    />
                                )}
                                <NewConnectionHandler />
                                <TransactionsPrefetcher />
                            </PlanManagementProvider>
                        </SupportChatProvider>
                    </ChatProvider>
                </DocumentTitleProvider>
            </NatsProvider>
        </FeatureFlagsIdentityContext.Provider>
    );
};
